/**
 * Getting past a stale copy of the game's code in the browser's HTTP cache.
 *
 * The dev server sends Vite's prebundled dependencies (`/node_modules/.vite/deps/*?v=<hash>`) as
 * cacheable for a year. When it rebuilds them (a restart after a dependency or config change, or
 * a dependency found while a page loads), a dependency can keep its URL while the chunks it
 * imports are renamed: the browser keeps using its old copy, whose dynamic imports the server now
 * answers with 504 ("Failed to fetch dynamically imported module"), and a plain reload does not
 * help, only a hard reload. `reloadFresh` does what the hard reload does for those files: it asks
 * the server again for every one the page loaded (`cache: 'reload'` replaces the cached copy),
 * then reloads.
 *
 * A build has none of this: its files are named by their content (cache/serviceWorker.ts keeps them).
 */

/** Where Vite serves its prebundled dependencies on the dev server. */
const DEPS = '/node_modules/.vite/deps/';
/** When the page last healed itself (sessionStorage), so a failure that stays is shown, not looped. */
const HEALED_KEY = 'nine-swords.healed.v1';
/** A second failure within this long of a self-heal is shown to the player. */
const HEAL_GAP_MS = 60_000;

/** The URLs among `urls` that are the dev server's prebundled dependencies, once each. */
export function depUrls(urls: Iterable<string>, base = globalThis.location?.href ?? 'http://localhost/'): string[] {
  const out = new Set<string>();
  for (const u of urls) {
    try {
      if (new URL(u, base).pathname.startsWith(DEPS)) out.add(u);
    } catch {
      // Not a URL.
    }
  }
  return [...out];
}

/** The URL a "Failed to fetch dynamically imported module: <url>" message names, if any. */
export function failedUrl(message: string): string | null {
  return /(https?:\/\/\S+)/.exec(message)?.[1]?.replace(/[)'",.]+$/, '') ?? null;
}

/**
 * Asks the server again for every prebundled dependency this page loaded (and `extra`), replacing
 * the browser's cached copies. Returns how many. Nothing on a build.
 */
export async function refreshModules(extra: readonly string[] = []): Promise<number> {
  if (!import.meta.env.DEV) return 0;
  let loaded: string[] = [];
  try {
    loaded = performance.getEntriesByType('resource').map((e) => e.name);
  } catch {
    // No resource timing: only `extra`.
  }
  const urls = depUrls([...loaded, ...extra]);
  await Promise.allSettled(urls.map((u) => fetch(u, { cache: 'reload' })));
  return urls.length;
}

/** Refreshes the page's cached code (`refreshModules`), then loads the page again. */
export async function reloadFresh(extra: readonly string[] = []): Promise<void> {
  try {
    await refreshModules(extra);
  } finally {
    location.reload();
  }
}

/**
 * Whether the page may heal itself now (refresh and reload without asking): once, unless it did so
 * within the last minute (then the failure stays and is shown). Marks the attempt.
 */
export function mayHealOnce(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(HEALED_KEY) ?? 0);
    if (now - last < HEAL_GAP_MS) return false;
    sessionStorage.setItem(HEALED_KEY, String(now));
    return true;
  } catch {
    // Storage blocked: never loop.
    return false;
  }
}
